import React, { useState } from 'react';
import {
    Container, Typography, Box, Paper, Button, Stepper, Step, StepLabel,
    Grid, TextField, FormControlLabel, Checkbox, Divider, Alert, List,
    ListItem, ListItemText, Radio, RadioGroup, FormControl, CircularProgress,
} from '@mui/material';
import { ArrowBack, ArrowForward, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import marketplaceService from '../services/marketplaceService';

const steps = ['Shipping Information', 'Payment Method', 'Review Order'];

const CheckoutPage = () => {
    const navigate = useNavigate();
    const { cartItems, getCartTotal, clearCart } = useCart();
    const [activeStep, setActiveStep] = useState(0);
    const [orderPlaced, setOrderPlaced] = useState(false);
    const [placedOrderId, setPlacedOrderId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orderError, setOrderError] = useState(null);

    const [shippingInfo, setShippingInfo] = useState({
        firstName: '', lastName: '', email: '', phone: '',
        address: '', city: '', state: '', zipCode: '', country: 'US',
    });
    const [paymentMethod, setPaymentMethod] = useState('credit_card');
    const [sameAsShipping, setSameAsShipping] = useState(true);
    const [validationError, setValidationError] = useState(null);

    const validateShipping = () => {
        const { firstName, lastName, email, address, city, state, zipCode } = shippingInfo;
        if (!firstName.trim() || !lastName.trim())
            return 'First and last name are required.';
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
            return 'A valid email address is required.';
        if (!address.trim() || !city.trim() || !state.trim() || !zipCode.trim())
            return 'Full shipping address is required.';
        if (!/^\d{4,10}$/.test(zipCode.replace(/\s-/g, '')))
            return 'Enter a valid ZIP / postal code.';
        return null;
    };

    const handleNext = () => {
        setValidationError(null);
        if (activeStep === 0) {
            const err = validateShipping();
            if (err) { setValidationError(err); return; }
        }
        if (activeStep === steps.length - 1) {
            handlePlaceOrder();
        } else {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handleBack = () => { setValidationError(null); setActiveStep((prev) => prev - 1); };

    const handlePlaceOrder = async () => {
        if (isSubmitting) return;          // guard against double-click
        setIsSubmitting(true);
        setOrderError(null);
        try {
            const order = await marketplaceService.createOrder(cartItems, shippingInfo, paymentMethod);
            clearCart();
            setPlacedOrderId(order.order_id);
            setOrderPlaced(true);
        } catch (err) {
            setOrderError(err.message || 'Failed to place order. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (e) => {
        setValidationError(null);
        setShippingInfo({ ...shippingInfo, [e.target.name]: e.target.value });
    };

    if (cartItems.length === 0 && !orderPlaced) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                    <Typography variant="h5" gutterBottom>Your cart is empty</Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        Add items to your cart before checking out.
                    </Typography>
                    <Button variant="contained" onClick={() => navigate('/parts')} sx={{ mt: 2 }}>
                        Continue Shopping
                    </Button>
                </Paper>
            </Container>
        );
    }

    if (orderPlaced) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                    <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom fontWeight="bold">Order Placed!</Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        Thank you! You'll receive a confirmation email shortly.
                    </Typography>
                    {placedOrderId && (
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Order ID: <strong>{placedOrderId}</strong>
                        </Typography>
                    )}
                    <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button variant="contained" onClick={() => navigate(`/orders/${placedOrderId}`)}>
                            View Order
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('/parts')}>
                            Continue Shopping
                        </Button>
                    </Box>
                </Paper>
            </Container>
        );
    }

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>Shipping Address</Typography>
                        {validationError && (
                            <Alert severity="error" sx={{ mb: 2 }}>{validationError}</Alert>
                        )}
                        <Grid container spacing={2}>
                            {[
                                { label: 'First Name', name: 'firstName', xs: 6 },
                                { label: 'Last Name',  name: 'lastName',  xs: 6 },
                                { label: 'Email',      name: 'email',     xs: 6, type: 'email' },
                                { label: 'Phone',      name: 'phone',     xs: 6 },
                                { label: 'Address',    name: 'address',   xs: 12 },
                                { label: 'City',       name: 'city',      xs: 6 },
                                { label: 'State',      name: 'state',     xs: 3 },
                                { label: 'ZIP Code',   name: 'zipCode',   xs: 3 },
                            ].map(({ label, name, xs, type }) => (
                                <Grid item xs={12} sm={xs} key={name}>
                                    <TextField required fullWidth label={label} name={name}
                                        type={type || 'text'} value={shippingInfo[name]}
                                        onChange={handleInputChange}
                                        inputProps={{ maxLength: 200 }} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                );
            case 1:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>Payment Method</Typography>
                        <FormControl component="fieldset">
                            <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                                <FormControlLabel value="credit_card" control={<Radio />} label="Credit Card" />
                                <FormControlLabel value="paypal"      control={<Radio />} label="PayPal" />
                                <FormControlLabel value="bank_transfer" control={<Radio />} label="Bank Transfer" />
                            </RadioGroup>
                        </FormControl>
                        <FormControlLabel
                            control={<Checkbox checked={sameAsShipping} onChange={(e) => setSameAsShipping(e.target.checked)} />}
                            label="Billing address same as shipping" sx={{ mt: 2 }}
                        />
                    </Box>
                );
            case 2:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>Review Your Order</Typography>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Shipping Address</Typography>
                            <Typography variant="body2">{shippingInfo.firstName} {shippingInfo.lastName}</Typography>
                            <Typography variant="body2">{shippingInfo.address}</Typography>
                            <Typography variant="body2">{shippingInfo.city}, {shippingInfo.state} {shippingInfo.zipCode}</Typography>
                            <Typography variant="body2">{shippingInfo.email}</Typography>
                        </Paper>
                        <Typography variant="subtitle2" gutterBottom>Order Items</Typography>
                        <List>
                            {cartItems.map((item) => (
                                <ListItem key={item.id} divider>
                                    <ListItemText
                                        primary={item.displayName || item.partNumber || item.mpn}
                                        secondary={`Qty: ${item.quantity}`}
                                    />
                                    <Typography variant="body2" fontWeight="bold">
                                        ${((item.unitPrice || item.price || 0) * item.quantity).toFixed(2)}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                        {orderError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {orderError}
                            </Alert>
                        )}
                    </Box>
                );
            default: return null;
        }
    };

    const subtotal = getCartTotal();
    const shipping = 10;
    const tax = subtotal * 0.08;

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold">Checkout</Typography>
            <Stepper activeStep={activeStep} sx={{ py: 3 }}>
                {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
            </Stepper>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, borderRadius: 3 }}>
                        {renderStepContent(activeStep)}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                            <Button disabled={activeStep === 0} onClick={handleBack} startIcon={<ArrowBack />}>
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={isSubmitting}
                                endIcon={isSubmitting
                                    ? <CircularProgress size={16} color="inherit" />
                                    : activeStep === steps.length - 1 ? <CheckCircle /> : <ArrowForward />
                                }
                            >
                                {activeStep === steps.length - 1 ? 'Place Order' : 'Next'}
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, borderRadius: 3, position: 'sticky', top: 80 }}>
                        <Typography variant="h6" gutterBottom fontWeight="bold">Order Summary</Typography>
                        <Divider sx={{ my: 2 }} />
                        {[
                            { label: 'Subtotal', value: `$${subtotal.toFixed(2)}` },
                            { label: 'Shipping', value: `$${shipping.toFixed(2)}` },
                            { label: 'Tax (8%)', value: `$${tax.toFixed(2)}` },
                        ].map(({ label, value }) => (
                            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">{label}</Typography>
                                <Typography variant="body2" fontWeight="bold">{value}</Typography>
                            </Box>
                        ))}
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="h6" fontWeight="bold">Total</Typography>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                                ${(subtotal + shipping + tax).toFixed(2)}
                            </Typography>
                        </Box>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="caption">Secure checkout — SSL encrypted</Typography>
                        </Alert>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default CheckoutPage;
